import { useSuspenseQuery } from "@apollo/client/react";
import { format, parseISO } from "date-fns";
import { ExternalLink, FlaskConical, Play } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { type ChangeRequestsBoolExp, OrderBy } from "~/common/types";
import { CopyIdsButton } from "~/components/copy-button";
import type { JobRun } from "~/components/job-log";
import { Badge, Button, Checkbox } from "~/components/ui";
import { BatchReviewDialog } from "~/features/changeRequests/components/BatchReviewDialog";
import { ChangeRequestStatusBadge } from "~/features/changeRequests/components/ChangeRequestStatusBadge";
import {
  GetChangeRequestsDocument,
  type GetChangeRequestsQuery,
  type GetChangeRequestsQueryVariables,
} from "~/features/changeRequests/gqls";
import { useChangeRequestProcessing } from "~/features/changeRequests/hooks";
import type { ChangeRequestRaw } from "~/features/changeRequests/types";
import { useDipLifecycle } from "~/features/dataImportProjects/context";
import type { DipPeekTarget, DipPeekType } from "~/features/dataImportProjects/types";
import { useTenant } from "~/features/tenant/context";
import { rootRoute as R } from "~/routes";
import { JsonKeyValues, type JsonRecord } from "./BatchEvidencesJobJson";
import { DipReadOnlyTooltip } from "./DipReadOnlyTooltip";

type BatchChangeRequestsPanelProps = {
  jobId: string;
  jobName: string | null;
  jobRuns: { id: string; statusId: string; createdAt: string }[];
  recordComposerDefinitionId?: string;
  onOpenLog: (jobRun: JobRun) => void;
  onOpenPeek?: (target: DipPeekTarget) => void;
  canExecuteMutation: () => boolean;
};

const LIMIT = 100;

function changeRequestsWhere(jobId: string, recordComposerDefinitionId: string | undefined): ChangeRequestsBoolExp {
  return {
    _and: [
      { batchChangeRequestsJobId: { _eq: jobId } },
      ...(recordComposerDefinitionId
        ? [
            {
              changeRequestRecordComposerDefinition: {
                recordComposerDefinitionId: { _eq: recordComposerDefinitionId },
              },
            },
          ]
        : []),
    ],
  };
}

function summarizeChangeRequest(changeRequest: ChangeRequestRaw): string {
  const primaryItem =
    changeRequest.items.find((item) => item.modelType === "entity" || item.modelType === "relation") ??
    changeRequest.items[0];
  if (!primaryItem) return changeRequest.description ?? changeRequest.id;
  const props =
    (primaryItem.properties as { properties?: Record<string, unknown> }).properties ?? primaryItem.properties;
  for (const key of ["title", "event_title", "name", "action"]) {
    const value = (props as Record<string, unknown>)[key];
    if (typeof value === "string" && value.length > 0) return value;
  }
  return `${primaryItem.modelType}:${primaryItem.operationType}`;
}

export function resolveReviewTargetIds(selectedIds: string[], selectedOpenIds: string[]): string[] {
  return selectedIds.length > 0 && selectedOpenIds.length === 0 ? selectedIds : selectedOpenIds;
}

function ComposerLink({
  changeRequest,
  onOpenPeek,
}: {
  changeRequest: ChangeRequestRaw;
  onOpenPeek?: (target: DipPeekTarget) => void;
}) {
  const composer = changeRequest.changeRequestRecordComposerDefinition?.recordComposerDefinition;
  if (!composer) return <span>-</span>;
  const type: DipPeekType | null = composer.entityComposerDefinition
    ? "entityComposer"
    : composer.relationComposerDefinition
      ? "relationComposer"
      : null;

  // Open the composer as a peek over the current pipeline instead of navigating away.
  return type && onOpenPeek ? (
    <button
      type="button"
      className="inline-flex items-center gap-1 text-left text-primary-700 hover:underline"
      onClick={() => onOpenPeek({ type, id: composer.id })}
    >
      {composer.name}
    </button>
  ) : (
    <span>{composer.name}</span>
  );
}

export function BatchChangeRequestsPanel({
  jobId,
  jobName,
  jobRuns,
  recordComposerDefinitionId,
  onOpenLog,
  onOpenPeek,
  canExecuteMutation,
}: BatchChangeRequestsPanelProps) {
  const { t } = useTranslation();
  const { isReadOnly } = useDipLifecycle();
  const { tenantId } = useTenant();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewDryRun, setReviewDryRun] = useState(false);
  const { trackJob, isProcessing } = useChangeRequestProcessing();
  const where = useMemo(
    () => changeRequestsWhere(jobId, recordComposerDefinitionId),
    [jobId, recordComposerDefinitionId],
  );

  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }

  const { data, refetch } = useSuspenseQuery<GetChangeRequestsQuery, GetChangeRequestsQueryVariables>(
    GetChangeRequestsDocument,
    {
      variables: {
        tenantId,
        limit: LIMIT,
        offset: 0,
        where,
        order_by: { createdAt: OrderBy.Desc },
      },
    },
  );
  const changeRequests = data.changeRequests;
  const openChangeRequests = changeRequests.filter((changeRequest) => changeRequest.statusId === "open");
  const openCount = data.openChangeRequestsAggregate.aggregate?.count ?? 0;
  const openIds = openChangeRequests.map((changeRequest) => changeRequest.id);
  const selectableOpenIds = openIds.filter((id) => !isProcessing(id));
  const selectedOpenIds = selectedIds.filter((id) => selectableOpenIds.includes(id));
  const reviewTargetIds = resolveReviewTargetIds(selectedIds, selectedOpenIds);
  const jobRunTimestampById = new Map(
    jobRuns.map((jobRun) => [jobRun.id, format(parseISO(jobRun.createdAt), "yyyy/MM/dd HH:mm")]),
  );
  useEffect(() => {
    if (!isReadOnly) return;
    setReviewDialogOpen(false);
    setSelectedIds([]);
  }, [isReadOnly]);

  const openReviewDialog = (dryRun: boolean) => {
    if (!canExecuteMutation()) return;
    setReviewDryRun(dryRun);
    setReviewDialogOpen(true);
  };

  const onCommitted = (jobRunId: string, ids: string[]) => {
    const trackedIds = ids.length > 0 ? ids : openIds;
    if (trackedIds.length > 0) {
      trackJob(jobRunId, trackedIds);
    }
    setSelectedIds([]);
    // Not awaited: refetch re-suspends this component, and the job runs async anyway.
    void refetch();
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-12-regular text-fg-3">
          {data.changeRequestsAggregate.aggregate?.totalCount ?? 0} {t("model.changeRequest.modelNames")} / {openCount}{" "}
          {t("ui.open")}
        </div>
        <div className="flex flex-wrap gap-2">
          <CopyIdsButton ids={changeRequests.map((changeRequest) => changeRequest.id)} />
          <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("ui.dryRun")}>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={openCount === 0 || isReadOnly}
              onClick={() => openReviewDialog(true)}
            >
              <FlaskConical className="size-3.5" />
              {t("ui.dryRun")}
              {reviewTargetIds.length > 0 ? ` (${reviewTargetIds.length})` : ""}
            </Button>
          </DipReadOnlyTooltip>
          <DipReadOnlyTooltip disabled={isReadOnly} ariaLabel={t("feature.dataImportProjects.detail.operation.runJob")}>
            <Button
              type="button"
              size="sm"
              disabled={openCount === 0 || isReadOnly}
              onClick={() => openReviewDialog(false)}
            >
              <Play className="size-3.5" />
              {t("feature.dataImportProjects.detail.operation.runJob")}
              {reviewTargetIds.length > 0 ? ` (${reviewTargetIds.length})` : ""}
            </Button>
          </DipReadOnlyTooltip>
        </div>
      </div>

      {changeRequests.length === 0 ? (
        <p className="rounded-[var(--radius-md)] border border-border-default p-4 text-12-regular text-fg-3">
          {t("feature.common.noData")}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-[var(--radius-md)] border border-border-default">
          <table className="w-full min-w-[1100px] border-collapse text-12-regular">
            <thead className="bg-bg-subtle text-fg-2">
              <tr>
                <th className="w-10 border-b border-border-default px-3 py-2 text-left font-semibold">
                  <Checkbox
                    checked={selectableOpenIds.length > 0 && selectedOpenIds.length === selectableOpenIds.length}
                    disabled={selectableOpenIds.length === 0 || isReadOnly}
                    onCheckedChange={(checked) => {
                      if (!canExecuteMutation()) return;
                      setSelectedIds(checked ? selectableOpenIds : []);
                    }}
                    aria-label={t("ui.select")}
                  />
                </th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                  {t("model.changeRequest.status")}
                </th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.composer")}</th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.jobRun")}</th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.summary")}</th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">
                  {t("model.common.createdAt")}
                </th>
                <th className="border-b border-border-default px-3 py-2 text-left font-semibold">{t("ui.actions")}</th>
              </tr>
            </thead>
            <tbody>
              {changeRequests.map((changeRequest) => {
                const processing = isProcessing(changeRequest.id);
                const selectable = changeRequest.statusId === "open" && !processing;
                const selected = selectedIds.includes(changeRequest.id);
                return (
                  <tr key={changeRequest.id} className="border-t border-border-default align-top first:border-t-0">
                    <td className="px-3 py-2">
                      <Checkbox
                        checked={selected}
                        disabled={!selectable || isReadOnly}
                        onCheckedChange={(checked) => {
                          if (!canExecuteMutation()) return;
                          setSelectedIds((prev) =>
                            checked
                              ? [...new Set([...prev, changeRequest.id])]
                              : prev.filter((id) => id !== changeRequest.id),
                          );
                        }}
                        aria-label={t("ui.select")}
                      />
                    </td>
                    <td className="px-3 py-2">
                      <ChangeRequestStatusBadge status={changeRequest.statusId} processing={processing} />
                    </td>
                    <td className="px-3 py-2">
                      <ComposerLink changeRequest={changeRequest} onOpenPeek={onOpenPeek} />
                    </td>
                    <td className="px-3 py-2 text-fg-2">
                      {changeRequest.batchChangeRequestsJobRun?.id ? (
                        <button
                          type="button"
                          className="text-left text-primary-700 hover:underline"
                          onClick={() =>
                            onOpenLog({
                              jobRunId: changeRequest.batchChangeRequestsJobRun?.id ?? "",
                              jobName: "BatchChangeRequestsJob",
                            })
                          }
                        >
                          {jobRunTimestampById.get(changeRequest.batchChangeRequestsJobRun.id) ??
                            format(parseISO(changeRequest.batchChangeRequestsJobRun.createdAt), "yyyy/MM/dd HH:mm")}
                        </button>
                      ) : (
                        "-"
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <div className="grid gap-1">
                        <p className="font-medium text-fg-1">{summarizeChangeRequest(changeRequest)}</p>
                        <div className="flex flex-wrap gap-1">
                          {changeRequest.items.slice(0, 3).map((item) => (
                            <Badge key={item.id} tone="default">
                              {item.modelType}:{item.operationType}
                            </Badge>
                          ))}
                        </div>
                        {changeRequest.context ? <JsonKeyValues value={changeRequest.context as JsonRecord} /> : null}
                      </div>
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-fg-2">
                      {t("date.long", { date: parseISO(changeRequest.createdAt) })}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        className="inline-flex items-center gap-1 text-primary-700 hover:underline"
                        to={R.changeRequests.detail.$buildPath({ params: { id: changeRequest.id } })}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {t("ui.view")}
                        <ExternalLink className="size-3" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!isReadOnly ? (
        <BatchReviewDialog
          open={reviewDialogOpen && !isReadOnly}
          onOpenChange={(open) => {
            if (!open || canExecuteMutation()) setReviewDialogOpen(open);
          }}
          dryRun={reviewDryRun}
          batchJobId={jobId}
          batchJobRunId={null}
          batchJobName={jobName}
          selectedIds={reviewTargetIds}
          onCommitted={onCommitted}
          onOpenJobRun={(jobRunId, dryRun) => onOpenLog({ jobRunId, jobName: "BatchChangeRequestReviewsJob", dryRun })}
          canSubmit={canExecuteMutation}
        />
      ) : null}
    </section>
  );
}
